/**
 * Reference mesher: one quad per visible block face.
 *
 * This is no longer what the game draws - greedyMesher.ts is. It stays as the
 * obviously-correct implementation to test the greedy one against: for any
 * chunk, both must produce exactly the same visible surface, and the greedy
 * mesher must do it with fewer quads. A subtle meshing bug is very hard to see
 * in a screenshot and trivial to catch against a reference.
 *
 * Pure maths over a padded volume. No world, no engine, no DOM.
 */

import { CHUNK_SIZE, paddedIndex } from "./chunk.js";
import {
  facesHiddenBy, isTranslucent, isBedRender, isBoxRender, isWallRender,
  isSlabRender, wallConnects,
  isStairs, stairsFacing, stairsFlipped, isWallTorch, wallTorchFacing, isGate,
  isShutter, shutterState, isTrebuchetRender, trebuchetFacing,
  WALL_POST, WALL_ARM, WALL_ARM_HEIGHT, type DoorFacing,
  isFluidRender, isModelRender, isShapeRender, isWater, waterHeight, isLava, lavaHeight,
  isOil, oilHeight,
  panelEdge, faceTile, blockDef,
} from "../block/registry.js";

export interface MeshData {
  positions: Float32Array;
  normals: Float32Array;
  /** Runs 0..width across a quad, so a merged quad repeats its texture. */
  uvs: Float32Array;
  /** Which texture-array layer each vertex samples. */
  layers: Float32Array;
  /** Baked directional shading, one float per vertex. Cheap fake lighting. */
  shades: Float32Array;
  /**
   * Sky and torchlight for each vertex, packed as sky * 16 + block.
   *
   * One attribute rather than two: they are four bits each, a float carries
   * both exactly, and the shader has to pull them apart anyway to scale one by
   * the hour and leave the other alone.
   */
  lights: Float32Array;
  /** Triangles for the opaque pass. */
  indices: Uint32Array;
  /**
   * Triangles for the blended pass, indexing the same vertices.
   *
   * Two index lists over one vertex buffer rather than two meshes: the
   * renderer turns them into two geometry groups, which is exactly what
   * groups are for and costs no duplicated vertices.
   */
  translucentIndices: Uint32Array;
}

interface Face {
  nx: number; ny: number; nz: number;
  /** 4 corners, counter-clockwise seen from outside. */
  corners: readonly [number, number, number][];
  kind: "top" | "bottom" | "side";
  /** Baked light multiplier so faces read as distinct without a light engine. */
  shade: number;
}

export const FACES: readonly Face[] = [
  { // +X  east
    nx: 1, ny: 0, nz: 0, kind: "side", shade: 0.80,
    corners: [[1, 0, 1], [1, 0, 0], [1, 1, 0], [1, 1, 1]],
  },
  { // -X  west
    nx: -1, ny: 0, nz: 0, kind: "side", shade: 0.80,
    corners: [[0, 0, 0], [0, 0, 1], [0, 1, 1], [0, 1, 0]],
  },
  { // +Y  top
    nx: 0, ny: 1, nz: 0, kind: "top", shade: 1.00,
    corners: [[0, 1, 1], [1, 1, 1], [1, 1, 0], [0, 1, 0]],
  },
  { // -Y  bottom
    nx: 0, ny: -1, nz: 0, kind: "bottom", shade: 0.55,
    corners: [[0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1]],
  },
  { // +Z  south
    nx: 0, ny: 0, nz: 1, kind: "side", shade: 0.90,
    corners: [[0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1]],
  },
  { // -Z  north
    nx: 0, ny: 0, nz: -1, kind: "side", shade: 0.70,
    corners: [[1, 0, 0], [0, 0, 0], [0, 1, 0], [1, 1, 0]],
  },
];

/**
 * Growable arrays a mesher fills as it goes.
 *
 * Shared so the naive and greedy meshers can emit plant geometry through the
 * same code - the one shape neither of them merges, and the one place a
 * divergence between them would not be caught by the surface comparison.
 */
export interface MeshAccumulator {
  positions: number[];
  normals: number[];
  uvs: number[];
  layers: number[];
  shades: number[];
  lights: number[];
  indices: number[];
  translucentIndices: number[];
  vertexCount: number;
}

export function newAccumulator(): MeshAccumulator {
  return {
    positions: [], normals: [], uvs: [], layers: [], shades: [], lights: [],
    indices: [], translucentIndices: [], vertexCount: 0,
  };
}

export function finishMesh(acc: MeshAccumulator): MeshData {
  return {
    positions: new Float32Array(acc.positions),
    normals: new Float32Array(acc.normals),
    uvs: new Float32Array(acc.uvs),
    layers: new Float32Array(acc.layers),
    shades: new Float32Array(acc.shades),
    lights: new Float32Array(acc.lights),
    indices: new Uint32Array(acc.indices),
    translucentIndices: new Uint32Array(acc.translucentIndices),
  };
}

/** The two diagonals of a unit cube, as corner offsets. */
const CROSS_QUADS: readonly (readonly [number, number, number])[][] = [
  [[0, 0, 0], [1, 0, 1], [1, 1, 1], [0, 1, 0]],
  [[1, 0, 0], [0, 0, 1], [0, 1, 1], [1, 1, 0]],
];

/**
 * Emit a plant: two quads standing on the cube's diagonals.
 *
 * Each is emitted twice with opposite winding so it is visible from both
 * sides. Doing it here rather than with a double-sided material keeps the
 * terrain material single-sided, which matters far more - back-face culling
 * is throwing away half of every chunk.
 *
 * Shading is flat: a plant has no meaningful face direction, and shading the
 * two diagonals differently makes it flicker as you walk around it.
 */
export function emitCross(
  acc: MeshAccumulator, x: number, y: number, z: number, layer: number,
  /** Packed sky * 16 + block for the cell the plant stands in. */
  light = 255,
): void {
  for (const quad of CROSS_QUADS) {
    for (const flip of [false, true]) {
      const order = flip ? [0, 3, 2, 1] : [0, 1, 2, 3];
      for (const c of order) {
        const corner = quad[c]!;
        acc.positions.push(x + corner[0], y + corner[1], z + corner[2]);
        acc.normals.push(0, 1, 0);
        // U follows the quad's horizontal span, V its height.
        acc.uvs.push(c === 1 || c === 2 ? 1 : 0, c >= 2 ? 1 : 0);
        acc.layers.push(layer);
        acc.shades.push(1);
        acc.lights.push(light);
      }
      const v = acc.vertexCount;
      acc.indices.push(v, v + 1, v + 2, v, v + 2, v + 3);
      acc.vertexCount += 4;
    }
  }
}

/**
 * Which way a wall torch leans, per facing: away from the wall it hangs on.
 *
 * Facing 0 is the north wall, at -Z, so the torch leans toward +Z.
 */
const TORCH_AWAY: readonly (readonly [number, number])[] =
  [[0, 1], [-1, 0], [0, -1], [1, 0]];

/** How much of the cell's width the stick takes. */
const TORCH_THIN = 0.55;
/** The foot and the head of it, as heights inside the cell. */
const TORCH_FOOT = 0.16;
const TORCH_HEAD = 0.88;
/** How far the foot sits from the middle, toward the wall. */
const TORCH_INTO = 0.34;

/**
 * Emit a смолоскип hung on a wall: the plant's cross, thinned and leaned.
 *
 * A torch on a wall used to be the same cross as a torch on the floor, which
 * put it upright in the middle of the cell next to the wall - a torch
 * hovering in the air with a gap behind it. "Смолоскипи не мають текстури
 * ставлення на стіну."
 *
 * Three transforms on the same two quads, in this order: narrow it about the
 * middle so it reads as a stick rather than a bush; sit it between the foot
 * and the head rather than filling the cell; and shear it, so the bottom is
 * against the wall and the top has come out to the middle. The shear is what
 * makes it a bracket rather than something stuck through the wall - and it
 * is a shear rather than a rotation on purpose: the flame at the top stays
 * level, which is where the light is coming from.
 */
export function emitWallTorch(
  acc: MeshAccumulator, x: number, y: number, z: number,
  facing: number, layer: number, light = 255,
): void {
  const [ax, az] = TORCH_AWAY[facing & 3]!;
  const tall = TORCH_HEAD - TORCH_FOOT;
  for (const quad of CROSS_QUADS) {
    for (const flip of [false, true]) {
      const order = flip ? [0, 3, 2, 1] : [0, 1, 2, 3];
      for (const c of order) {
        const corner = quad[c]!;
        const up = TORCH_FOOT + corner[1]! * tall;
        // Nothing at the foot, everything by the head: the lean.
        const into = TORCH_INTO * (1 - corner[1]!);
        const px = 0.5 + (corner[0]! - 0.5) * TORCH_THIN - ax * into;
        const pz = 0.5 + (corner[2]! - 0.5) * TORCH_THIN - az * into;
        acc.positions.push(x + px, y + up, z + pz);
        acc.normals.push(0, 1, 0);
        acc.uvs.push(c === 1 || c === 2 ? 1 : 0, c >= 2 ? 1 : 0);
        acc.layers.push(layer);
        acc.shades.push(1);
        acc.lights.push(light);
      }
      const v = acc.vertexCount;
      acc.indices.push(v, v + 1, v + 2, v, v + 2, v + 3);
      acc.vertexCount += 4;
    }
  }
}

/** How thick a door panel is, in blocks. Three pixels of sixteen. */
export const DOOR_THICKNESS = 3 / 16;

/** How tall a bed stands. Low enough to step onto without jumping. */
export const BED_HEIGHT = 9 / 16;

/**
 * How much shorter a хвіртка is than a door, top and bottom.
 *
 * A tenth of the cell off each end. A gate in a тин is not a door in a wall:
 * it is a woven panel hung between two stakes, with daylight over it and a
 * gap under it that a hen gets through and you do not.
 */
export const GATE_TRIM = 0.1;

/**
 * The cell-local bounds of a hinged panel - a door or a shutter.
 *
 * It lies against whichever wall the thing currently occupies: the opening
 * when shut, the wall beside it when open.
 *
 * A хвіртка is the same panel, cut short at both ends. Done here because
 * this is the one place that decides what shape a panel is - the mesher, the
 * culling and the highlight box all come through it.
 */
export function panelBounds(
  id: number,
): { min: [number, number, number]; max: [number, number, number] } {
  const bounds = edgeBounds(panelEdge(id), DOOR_THICKNESS);
  if (!isGate(id)) return bounds;
  return {
    min: [bounds.min[0], GATE_TRIM, bounds.min[2]],
    max: [bounds.max[0], 1 - GATE_TRIM, bounds.max[2]],
  };
}

/** A slab of thickness `t` lying against one of the cell's four vertical edges. */
export function edgeBounds(
  edge: DoorFacing, t: number,
): { min: [number, number, number]; max: [number, number, number] } {
  switch (edge) {
    case 0: return { min: [0, 0, 0], max: [1, 1, t] };       // north, -Z
    case 1: return { min: [1 - t, 0, 0], max: [1, 1, 1] };   // east, +X
    case 2: return { min: [0, 0, 1 - t], max: [1, 1, 1] };   // south, +Z
    default: return { min: [0, 0, 0], max: [t, 1, 1] };      // west, -X
  }
}

/**
 * Emit a box with arbitrary bounds inside one cell.
 *
 * Reuses the FACES table above by reading its 0/1 corner offsets as "min" and
 * "max" rather than as cube corners, so the winding and the per-direction
 * shading are shared with the ordinary cube path and cannot drift from it.
 *
 * UVs run in block units rather than 0..1, so the texture keeps its scale: a
 * door's face samples a whole tile, and its three-pixel edge samples three
 * pixels of one.
 */
export function emitBox(
  acc: MeshAccumulator,
  x: number, y: number, z: number,
  min: readonly [number, number, number],
  max: readonly [number, number, number],
  id: number,
  /**
   * What is in the neighbouring cell, for culling. Omitted for doors and beds,
   * which are thin enough that every face of them is worth drawing; water is
   * not, and a stream that emits both sides of every internal boundary is both
   * slow and visibly darker where the faces overlap in the blend.
   */
  neighbour?: (dx: number, dy: number, dz: number) => number,
  /** Drop a face outright, whatever the neighbour is. Used where two boxes of
   * one shape meet: coincident surfaces fight for the depth buffer. */
  covered?: (nx: number, ny: number, nz: number) => boolean,
  /**
   * Packed sky * 16 + block, per face direction.
   *
   * A function rather than one value: a door lit from the corridor on one
   * side and from a dark room on the other has to say so, and the cell a face
   * looks into is the one that knows.
   */
  lightAt: (nx: number, ny: number, nz: number) => number = () => 255,
  /**
   * How many blocks of texture one block of face gets, for things drawn
   * larger than life.
   *
   * One everywhere but the требушет, which is drawn three times the size of
   * the cell it stands on: at 1 its timbers wore three repeats of the grain
   * each and read as dashed lines rather than as beams. A third of a repeat
   * per block of face puts the same grain on them that they had when the
   * whole engine was one block.
   */
  uvScale = 1,
): void {
  const target = isTranslucent(id) ? acc.translucentIndices : acc.indices;
  for (const face of FACES) {
    if (covered?.(face.nx, face.ny, face.nz) === true) continue;
    if (neighbour !== undefined && hiddenBy(
      id, neighbour(face.nx, face.ny, face.nz),
      neighbour(0, 1, 0), neighbour(face.nx, face.ny + 1, face.nz),
      face.ny,
    )) {
      continue;
    }
    const axis = face.nx !== 0 ? 0 : face.ny !== 0 ? 1 : 2;
    // Per face, not one layer for the whole box: a bed's pillow, rail and
    // headboard are three different textures on one block.
    const layer = faceTile(id, axis, face.nx + face.ny + face.nz > 0);
    // Upright faces map v to height; the top and bottom map it to depth.
    const uAxis = axis === 0 ? 2 : 0;
    const vAxis = axis === 1 ? 2 : 1;
    const light = lightAt(face.nx, face.ny, face.nz);

    for (const corner of face.corners) {
      const px = corner[0] === 1 ? max[0] : min[0];
      const py = corner[1] === 1 ? max[1] : min[1];
      const pz = corner[2] === 1 ? max[2] : min[2];
      acc.positions.push(x + px, y + py, z + pz);
      acc.normals.push(face.nx, face.ny, face.nz);

      const at = [px, py, pz];
      acc.uvs.push(
        (at[uAxis]! - min[uAxis]!) * uvScale,
        (at[vAxis]! - min[vAxis]!) * uvScale);
      acc.layers.push(layer);
      acc.shades.push(face.shade);
      acc.lights.push(light);
    }
    const v = acc.vertexCount;
    target.push(v, v + 1, v + 2, v, v + 2, v + 3);
    acc.vertexCount += 4;
  }
}

/**
 * How much of its cell this water actually fills, 0..1.
 *
 * The same number fluidBounds draws with, which is the point of it existing
 * separately: culling used to ask waterHeight - the height the *level* implies
 * - while the geometry was drawn at the height the *context* gives. Under the
 * surface every cell is full, so the geometry agreed everywhere and the
 * culling did not, and swimming through a lake was swimming through a maze of
 * blue panes.
 */
export function waterFill(id: number, above: number): number {
  /*
   * Лава on the same terms as water, and never mixed with it.
   *
   * A cell of лава under a cell of water is not a full cell of лава - they
   * are two different fluids and neither holds the other up. Asked of the
   * same kind only, so a stream of лава running into a pool of water reads
   * as two things meeting rather than as one continuous surface.
   */
  if (isLava(id)) return isLava(above) ? 1 : lavaHeight(id);
  // And нафта, which is a third fluid and holds up nothing but itself.
  if (isOil(id)) return isOil(above) ? 1 : oilHeight(id);
  return isWater(above) ? 1 : waterHeight(id);
}

/**
 * Is this face hidden by what is on the other side of it?
 *
 * Everything that is not water defers to the registry. Water needs the two
 * cells' fill levels *and* the direction of the face, because water sits at
 * the bottom of its cell:
 *
 *   - a top face is covered as soon as there is any water above it, since that
 *     water starts flush with this cell's ceiling;
 *   - a bottom face is covered only if the water below reaches its own ceiling;
 *   - a side face is covered by anything standing at least as high.
 */
export function hiddenBy(
  id: number, neighbour: number,
  above: number, neighbourAbove: number,
  dy: number,
): boolean {
  if (isWater(id) && isWater(neighbour)) {
    if (dy > 0) return true;
    if (dy < 0) return waterFill(neighbour, neighbourAbove) >= 1;
    return waterFill(neighbour, neighbourAbove) >= waterFill(id, above);
  }
  return facesHiddenBy(id, neighbour);
}

/**
 * How much of its cell a fluid fills.
 *
 * Height comes from the level, except when there is water directly above: a
 * column falling through the air fills its cell completely, whatever level it
 * carries. Without that, a waterfall is a stack of separate sloping slabs with
 * daylight between them.
 */
export function fluidBounds(
  id: number, above: number,
): { min: [number, number, number]; max: [number, number, number] } {
  return { min: [0, 0, 0], max: [1, waterFill(id, above), 1] };
}

/**
 * The cell-local bounds of anything the mesher draws as a box.
 *
 * A door or shutter is a panel against one wall; a bed is a low slab.
 */
export function boxBounds(
  id: number,
): { min: [number, number, number]; max: [number, number, number] } {
  if (isBedRender(id)) {
    return { min: [0, 0, 0], max: [1, BED_HEIGHT, 1] };
  }
  return panelBounds(id);
}

/**
 * A wall: a post in the middle, and an arm toward everything it touches.
 *
 * The arms are what make a wall a wall rather than a row of pillars, and they
 * are worked out here rather than stored, because the mesher is the one place
 * that can see all four neighbours at once. Placing a wall beside one changes
 * the look of both without either having to be told.
 *
 * Faces where two boxes meet are dropped rather than drawn. Two coincident
 * surfaces in the same plane fight for the depth buffer and flicker, and there
 * are four of them per arm.
 */
function emitWall(
  acc: MeshAccumulator, x: number, y: number, z: number, id: number,
  neighbour: (dx: number, dy: number, dz: number) => number,
  light: (dx: number, dy: number, dz: number) => number = () => 255,
): void {
  const lo = (1 - WALL_POST) / 2;
  const hi = 1 - lo;
  const armLo = (1 - WALL_ARM) / 2;
  const armHi = 1 - armLo;

  /** -Z, +X, +Z, -X: the same order every facing in this game uses. */
  const arms: readonly (readonly [number, number])[] = [[0, -1], [1, 0], [0, 1], [-1, 0]];
  const joined = arms.map(([dx, dz]) => wallConnects(neighbour(dx, 0, dz)));

  // The post. Its side faces are dropped where an arm covers them.
  emitBox(acc, x, y, z, [lo, 0, lo], [hi, 1, hi], id,
    undefined, (nx, _ny, nz) => {
      if (nz < 0) return joined[0] === true;
      if (nx > 0) return joined[1] === true;
      if (nz > 0) return joined[2] === true;
      if (nx < 0) return joined[3] === true;
      return false;
    }, light);

  arms.forEach(([dx, dz], i) => {
    if (joined[i] !== true) return;
    const min: [number, number, number] = [
      dx < 0 ? 0 : dx > 0 ? hi : armLo,
      0,
      dz < 0 ? 0 : dz > 0 ? hi : armLo,
    ];
    const max: [number, number, number] = [
      dx < 0 ? lo : dx > 0 ? 1 : armHi,
      WALL_ARM_HEIGHT,
      dz < 0 ? lo : dz > 0 ? 1 : armHi,
    ];
    // Both ends are covered: the inner one by the post, the outer one by
    // whatever the arm reached for - which is the only reason it exists.
    emitBox(acc, x, y, z, min, max, id, undefined,
      (nx, _ny, nz) => (nx === dx && nz === dz) || (nx === -dx && nz === -dz),
      light);
  });
}

/**
 * Требушет: a frame of timber on four wheels, out of two dozen boxes.
 *
 * "Так як він виглядає коли встановлений - дуже погано." It was a cube with
 * a picture of a требушет painted on its sides, which from anywhere but
 * dead-on is a crate with a drawing on it.
 *
 * Written in the machine's own coordinates - facing -Z, the way everything
 * in this game with a front does - and turned by `facing` on the way out, so
 * the shape is described once and the four ways of standing it cost nothing
 * to read.
 *
 * The arm is a staircase of little boxes because the mesher emits
 * axis-aligned ones and nothing else. Eight steps is enough that it reads as
 * a beam at an angle rather than as a flight of stairs, at the size a block
 * is on the screen.
 */
/**
 * How many blocks across the engine is drawn, against the one it stands on.
 *
 * Three, because one looked like a toy - see `emitTrebuchet`.
 */
export const TREBUCHET_SIZE = 3;

function emitTrebuchet(
  acc: MeshAccumulator, x: number, y: number, z: number, id: number,
  light: (dx: number, dy: number, dz: number) => number,
): void {
  const facing = trebuchetFacing(id);
  /**
   * Turn a box from the machine's own frame into the cell's, three times over.
   *
   * "Требушет дуже малий, зроби його втричі більшим." It was drawn inside its
   * own block and looked like a model of one - a siege engine you could step
   * over. Everything below is still written in a unit cube, which is the only
   * sane way to draw a machine; `TREBUCHET_SIZE` is where it is made big.
   *
   * It grows from the middle of its cell and up from the floor of it, so the
   * block you put down is under the bed of the engine and the rest of it
   * stands round and above - which is what a thing three blocks wide has to
   * do when only one of those blocks is real. What it does *not* do is take
   * up the other cells: they stay walkable, the engine is worked from the
   * block it stands on, and a siege engine you can walk through the legs of
   * is a fair price for one you can see.
   */
  const put = (
    x0: number, y0: number, z0: number, x1: number, y1: number, z1: number,
  ): void => {
    const spin = (px: number, pz: number): [number, number] => {
      switch (facing) {
        case 1: return [1 - pz, px];
        case 2: return [1 - px, 1 - pz];
        case 3: return [pz, 1 - px];
        default: return [px, pz];
      }
    };
    const wide = (v: number): number => 0.5 + (v - 0.5) * TREBUCHET_SIZE;
    const a = spin(x0, z0);
    const b = spin(x1, z1);
    emitBox(acc, x, y, z,
      [wide(Math.min(a[0], b[0])), y0 * TREBUCHET_SIZE, wide(Math.min(a[1], b[1]))],
      [wide(Math.max(a[0], b[0])), y1 * TREBUCHET_SIZE, wide(Math.max(a[1], b[1]))],
      id, undefined, undefined, light, 1 / TREBUCHET_SIZE);
  };

  /*
   * Drawn from the картa предмету Pavlo sent: four views of a катапульта.
   *
   * What was here before read as a flight of stairs with a cup on it. The
   * drawing is a voxel model too - its diagonals are stepped, as any diagonal
   * in a world of boxes must be - and the difference is thickness: its
   * timbers are a good eighth of the machine, so a stepped edge reads as a
   * rough beam rather than as a staircase. Everything below is fat.
   *
   * The machine, from the drawing:
   *
   *   - a rectangle of beams on the ground, with a fat wheel at each corner;
   *   - an A-frame each side: a post leaning back and a long brace from the
   *     front of the bed, meeting at the top where the arm is hung;
   *   - a beam across the two tops, which is the axle;
   *   - the arm from that axle, up and forward, with an open cup on the end;
   *   - and the rope from the cup down to the front of the bed.
   *
   * Written in a unit cube and drawn three times that - see `put` - so these
   * are fractions of the engine rather than blocks. Forward is -Z: that is
   * where the cup goes, and where it throws.
   */
  const T = 0.085;

  /**
   * A beam that is not along an axis, which is every interesting one.
   *
   * Boxes that merely touch at a corner leave a hairline of sky through the
   * join; boxes that overlap have all their long faces in one plane and two
   * coplanar faces fight for the depth buffer. So they overlap by half their
   * length, each one is thick enough to cover the diagonal band it sits on -
   * a beam of thickness t running at a slope needs a box about 1.5t across -
   * and every other one is a hundredth of a block narrower so no two long
   * faces ever share a plane.
   */
  const beam = (
    from: readonly [number, number, number],
    to: readonly [number, number, number],
    thick: number,
  ): void => {
    const run = Math.hypot(to[0] - from[0], to[1] - from[1], to[2] - from[2]);
    const steps = Math.max(2, Math.ceil(run / (thick * 0.4)));
    const box = thick * 1.15;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const nudge = (i % 2) * 0.004;
      const at = [0, 1, 2].map((a2) => from[a2]! + (to[a2]! - from[a2]!) * t);
      put(at[0]! - box / 2 + nudge, at[1]! - box / 2, at[2]! - box / 2 + nudge,
        at[0]! + box / 2 - nudge, at[1]! + box / 2, at[2]! + box / 2 - nudge);
    }
  };

  /* ---- the bed, and the wheels at its corners ---------------------------- */

  // Two side beams the length of it, two across the ends.
  for (const bx of [0.14, 0.73]) put(bx, 0.10, 0.06, bx + T, 0.23, 0.94);
  for (const bz of [0.06, 0.81]) put(0.14, 0.10, bz, 0.86, 0.23, bz + T);

  /*
   * Fat wheels, proud of the bed on both sides.
   *
   * A wheel in the drawing is a cube with an axle on the outside of it - as
   * round as a voxel wheel gets, and rounder than anything three blocks of
   * engine could show.
   */
  for (const wx of [0.00, 0.83]) {
    for (const wz of [0.10, 0.72]) {
      put(wx, 0.00, wz, wx + 0.17, 0.26, wz + 0.18);
      // The axle, a stub out of the middle of the hub.
      put(wx - 0.04, 0.09, wz + 0.05, wx + 0.21, 0.17, wz + 0.13);
    }
  }

  /* ---- the frame: a post and a brace each side, and the axle across ------ */

  /*
   * Four legs from the four corners of the bed, leaning in to one axle.
   *
   * Every view in the drawing is an A - the front as well as the side - so
   * the legs splay both ways, which is the difference between a frame and
   * two hurdles standing parallel. The first version leaned them only fore
   * and aft and the front view came out as a pair of sticks.
   */
  const topL: readonly [number, number, number] = [0.34, 0.74, 0.52];
  const topR: readonly [number, number, number] = [0.66, 0.74, 0.52];
  for (const [corner, top] of [
    [[0.21, 0.22, 0.18], topL], [[0.21, 0.22, 0.82], topL],
    [[0.79, 0.22, 0.18], topR], [[0.79, 0.22, 0.82], topR],
  ] as const) {
    beam(corner, top, T);
  }
  // The axle across the two tops, which the arm hangs on.
  put(0.30, 0.70, 0.47, 0.70, 0.79, 0.57);
  // And a tie across the legs a third of the way up, as in the front view.
  put(0.26, 0.40, 0.52, 0.74, 0.49, 0.62);

  /* ---- the arm, and what it carries -------------------------------------- */

  // From the axle, up and forward over the front of the bed.
  beam([0.50, 0.72, 0.52], [0.50, 1.06, 0.12], 0.12);

  /*
   * The cup: four walls and a floor, open at the top.
   *
   * Hollow, because the drawing's is - it is the one part of the machine you
   * look *into*, and a solid lump there reads as a counterweight, which is a
   * different engine.
   */
  const cup = { x0: 0.32, x1: 0.68, y: 1.00, z0: -0.02, z1: 0.28 };
  put(cup.x0, cup.y, cup.z0, cup.x1, cup.y + 0.06, cup.z1);
  for (const wall of [
    [cup.x0, cup.z0, cup.x0 + 0.06, cup.z1],
    [cup.x1 - 0.06, cup.z0, cup.x1, cup.z1],
    [cup.x0, cup.z0, cup.x1, cup.z0 + 0.06],
    [cup.x0, cup.z1 - 0.06, cup.x1, cup.z1],
  ]) {
    put(wall[0]!, cup.y, wall[1]!, wall[2]!, cup.y + 0.20, wall[3]!);
  }

  // And the rope, from under the cup down to the front of the bed.
  beam([0.50, 0.98, 0.18], [0.50, 0.28, 0.34], 0.06);
}

/**
 * Ставні: two leaves, and they swing outward.
 *
 * "Мають відкриватись назовні а також ділитись на дві частини: ліва половина
 * відкривається вліво а права вправо." A shutter was one panel filling the
 * whole opening and swinging as one - which is a door in a window, not a
 * pair of shutters.
 *
 * Shut, the two leaves meet in the middle of the opening. Open, each lies
 * along its own side of it, and *outside*: the facing points out of the wall
 * - the face that was clicked - so the sides it swings to are the outside
 * ones. That half of it came free with hanging things on the wall they were
 * clicked on rather than on the one the player happened to face.
 */
function emitShutter(
  acc: MeshAccumulator, x: number, y: number, z: number, id: number,
  light: (dx: number, dy: number, dz: number) => number,
): void {
  const { facing, open } = shutterState(id);
  const t = DOOR_THICKNESS;
  if (!open) {
    const b = edgeBounds(facing, t);
    // Which way the opening runs: a panel on a north or south wall spans X,
    // one on an east or west wall spans Z.
    const across = facing === 0 || facing === 2 ? 0 : 2;
    for (const half of [0, 1]) {
      const min: [number, number, number] = [...b.min];
      const max: [number, number, number] = [...b.max];
      if (half === 0) max[across] = 0.5;
      else min[across] = 0.5;
      emitBox(acc, x, y, z, min, max, id, undefined, undefined, light);
    }
    return;
  }
  // Open: one leaf against each side of the opening, a quarter turn either
  // way from where they were shut.
  for (const turn of [1, 3]) {
    const b = edgeBounds(((facing + turn) % 4) as DoorFacing, t);
    emitBox(acc, x, y, z, b.min, b.max, id, undefined, undefined, light);
  }
}

/**
 * Сходи: a slab across the whole cell, and half a slab on top of it.
 *
 * The high half is toward the facing, so a run of them climbs that way. Two
 * boxes rather than a shape of its own, for the reason a wall is a post and
 * some arms: `emitBox` already knows how to light a box and how to drop the
 * faces where two of them meet, and a third way of making geometry would be
 * a third way of getting the winding wrong.
 *
 * The faces between the two halves are dropped: two coincident surfaces in
 * one plane fight for the depth buffer and flicker.
 */
function emitStairs(
  acc: MeshAccumulator, x: number, y: number, z: number, id: number,
  light: (dx: number, dy: number, dz: number) => number = () => 255,
): void {
  /*
   * Upside down, the whole thing is mirrored about the middle of the cell.
   *
   * "Сходи можливо було ставити перевернуто якщо ставляться до блока знизу
   * вверх." A stair hung under a floor has its slab at the top and the step
   * beneath it - which is the same two boxes with their heights swapped, so
   * the shape is written once and flipped on the way out.
   */
  const over = stairsFlipped(id);
  const flip = (lo: number, hi: number): [number, number] =>
    over ? [1 - hi, 1 - lo] : [lo, hi];

  // The full slab: the whole footprint, half the height, at the bottom - or
  // at the top, turned over.
  const [sy0, sy1] = flip(0, 0.5);
  emitBox(acc, x, y, z, [0, sy0, 0], [1, sy1, 1], id, undefined, undefined, light);

  /** -Z, +X, +Z, -X: the same order every facing in this game uses. */
  const facing = stairsFacing(id);
  const dx = facing === 1 ? 1 : facing === 3 ? -1 : 0;
  const dz = facing === 0 ? -1 : facing === 2 ? 1 : 0;
  const [hy0, hy1] = flip(0.5, 1);
  const min: [number, number, number] = [
    dx > 0 ? 0.5 : 0, hy0, dz > 0 ? 0.5 : 0,
  ];
  const max: [number, number, number] = [
    dx < 0 ? 0.5 : 1, hy1, dz < 0 ? 0.5 : 1,
  ];
  // The face against the slab is never seen: below it the ordinary way up,
  // above it when the thing is hanging.
  emitBox(acc, x, y, z, min, max, id, undefined,
    (nx, ny, nz) => { void nx; void nz; return over ? ny > 0 : ny < 0; }, light);
}

/** Emit whatever a non-cube block is: a plant, a hinged panel, a bed, water. */
export function emitShape(
  acc: MeshAccumulator, x: number, y: number, z: number, id: number,
  neighbour?: (dx: number, dy: number, dz: number) => number,
  /**
   * Packed sky * 16 + block for a cell offset from this one.
   *
   * Defaults to full daylight, so a caller with no light field gets the world
   * as it looked before there was any.
   */
  light: (dx: number, dy: number, dz: number) => number = () => 255,
): void {
  // A model the scene stands there; nothing of it is meshed.
  if (isModelRender(id)) return;
  if (isFluidRender(id)) {
    const { min, max } = fluidBounds(id, neighbour?.(0, 1, 0) ?? 0);
    emitBox(acc, x, y, z, min, max, id, neighbour, undefined, light);
    return;
  }
  if (isStairs(id)) {
    emitStairs(acc, x, y, z, id, light);
    return;
  }
  if (isWallRender(id)) {
    /*
     * A wall with nobody to ask about is a lone post, not a cross.
     *
     * The guard here used to be `&& neighbour !== undefined`, and with walls
     * still counted as boxes below that was harmless: a caller with no
     * neighbours got a box. They are not boxes any more, so a missing
     * neighbour function would drop a wall all the way through to the plant
     * branch and draw it as two crossed quads.
     */
    emitWall(acc, x, y, z, id, neighbour ?? (() => 0), light);
    return;
  }
  if (isTrebuchetRender(id)) {
    emitTrebuchet(acc, x, y, z, id, light);
    return;
  }
  if (isShutter(id)) {
    emitShutter(acc, x, y, z, id, light);
    return;
  }
  if (isSlabRender(id)) {
    // Half a cube, on the floor of its cell. The bottom and the four sides
    // meet whatever is beside them, so the faces are dropped the same way a
    // full block's are; the top never is.
    emitBox(acc, x, y, z, [0, 0, 0], [1, 0.5, 1], id, neighbour, undefined, light);
    return;
  }
  if (isBoxRender(id)) {
    const { min, max } = boxBounds(id);
    // A bed shows a different tile on each face, so the box is emitted
    // face by face rather than with one layer for all six.
    emitBox(acc, x, y, z, min, max, id, undefined, undefined, light);
    return;
  }
  if (isWallTorch(id)) {
    emitWallTorch(
      acc, x, y, z, wallTorchFacing(id), blockDef(id).faces.side, light(0, 0, 0));
    return;
  }
  // A plant or a torch takes the light of the cell it stands in: it fills no
  // face, so there is no "other side" to read.
  emitCross(acc, x, y, z, blockDef(id).faces.side, light(0, 0, 0));
}

/** One quad per exposed face. Correct, and deliberately unclever. */
export function meshChunkNaive(volume: Uint16Array): MeshData {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const layers: number[] = [];
  const shades: number[] = [];
  const lights: number[] = [];
  const indices: number[] = [];
  const translucentIndices: number[] = [];
  /**
   * Plants and doors are emitted after the cube faces, in scan order, so both
   * meshers produce them identically.
   */
  const shapes: [number, number, number, number][] = [];

  const at = (x: number, y: number, z: number): number =>
    volume[paddedIndex(x, y, z)]!;

  let vertexCount = 0;

  for (let y = 0; y < CHUNK_SIZE; y++) {
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const id = at(x, y, z);
        if (id === 0) continue;
        if (isShapeRender(id)) {
          shapes.push([x, y, z, id]);
          continue;
        }

        for (const face of FACES) {
          if (hiddenBy(
            id, at(x + face.nx, y + face.ny, z + face.nz),
            at(x, y + 1, z), at(x + face.nx, y + face.ny + 1, z + face.nz),
            face.ny,
          )) continue;

          // Not def.faces[face.kind]: a block with a facing shows its front
          // on one wall, and only the registry knows which.
          const axis = face.nx !== 0 ? 0 : face.ny !== 0 ? 1 : 2;
          const layer = faceTile(id, axis, face.nx + face.ny + face.nz > 0);
          const cornerUv: readonly [number, number][] = [[0, 0], [1, 0], [1, 1], [0, 1]];

          for (let i = 0; i < 4; i++) {
            const c = face.corners[i]!;
            positions.push(x + c[0], y + c[1], z + c[2]);
            normals.push(face.nx, face.ny, face.nz);
            uvs.push(cornerUv[i]![0], cornerUv[i]![1]);
            layers.push(layer);
            shades.push(face.shade);
            lights.push(255);
          }
          const target = isTranslucent(id) ? translucentIndices : indices;
          target.push(
            vertexCount, vertexCount + 1, vertexCount + 2,
            vertexCount, vertexCount + 2, vertexCount + 3,
          );
          vertexCount += 4;
        }
      }
    }
  }

  const acc: MeshAccumulator = {
    positions, normals, uvs, layers, shades, lights, indices, translucentIndices,
    vertexCount,
  };
  for (const [x, y, z, id] of shapes) {
    emitShape(acc, x, y, z, id,
      (dx, dy, dz) => at(x + dx, y + dy, z + dz));
  }

  return finishMesh(acc);
}
